import { GridIcon } from '../../icons/GridIcon';
import { ListIcon } from '../../icons/ListIcon';
import { ToggleButton, ToggleLabel, ToggleWrapper } from './ViewToggle.styles';
import type { ViewMode, ViewToggleOption, ViewToggleProps } from './ViewToggle.types';

const DEFAULT_OPTIONS: ViewToggleOption<ViewMode>[] = [
  { value: 'grid', label: 'Grid view', Icon: GridIcon },
  { value: 'compact', label: 'Compact view', Icon: ListIcon },
];

type Segment = ViewToggleOption<string> & { active: boolean };

/**
 * The two modes cannot share one attribute: `aria-pressed` is invalid on an anchor, so navigated
 * mode marks the current option with `aria-current` instead.
 */
export function ViewToggle(props: ViewToggleProps<ViewMode>): JSX.Element;
export function ViewToggle<T extends string>(
  props: ViewToggleProps<T> & { options: ViewToggleOption<T>[] }
): JSX.Element;
export function ViewToggle<T extends string>(props: ViewToggleProps<T>) {
  const { value, options, labels = 'hidden', gridLabel, compactLabel } = props;
  const resolvedOptions: ViewToggleOption<string>[] = options ?? DEFAULT_OPTIONS;
  const labelOverrides: Record<string, string | undefined> = options
    ? {}
    : { grid: gridLabel, compact: compactLabel };

  const segments: Segment[] = resolvedOptions.map((option) => ({
    ...option,
    label: labelOverrides[option.value] ?? option.label,
    active: option.value === value,
  }));

  const labelsVisible = labels === 'visible';
  const renderContent = ({ Icon, label }: Segment) => (
    <>
      <Icon />
      {labelsVisible ? <ToggleLabel>{label}</ToggleLabel> : null}
    </>
  );

  if (props.hrefFor) {
    const { hrefFor, linkComponent = 'a', navigationLabel } = props;

    return (
      <ToggleWrapper as="nav" aria-label={navigationLabel}>
        {segments.map((segment) => (
          <ToggleButton
            key={segment.value}
            as={linkComponent}
            href={hrefFor(segment.value as T)}
            $active={segment.active}
            aria-current={segment.active ? 'page' : undefined}
            aria-label={labelsVisible ? undefined : segment.label}
          >
            {renderContent(segment)}
          </ToggleButton>
        ))}
      </ToggleWrapper>
    );
  }

  const { onChange } = props;

  return (
    <ToggleWrapper role="group">
      {segments.map((segment) => (
        <ToggleButton
          key={segment.value}
          as="button"
          type="button"
          $active={segment.active}
          aria-label={labelsVisible ? undefined : segment.label}
          aria-pressed={segment.active}
          onClick={() => onChange(segment.value as T)}
        >
          {renderContent(segment)}
        </ToggleButton>
      ))}
    </ToggleWrapper>
  );
}
